import { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'

const FIELD_LABELS = {
  employer_name: 'Employer Name', employer_ein: 'Employer EIN',
  employee_name: 'Employee Name', employee_ssn_last4: 'SSN (Last 4)',
  wages_box1: 'Wages (Box 1)', federal_tax_withheld_box2: 'Federal Tax Withheld (Box 2)',
  social_security_wages_box3: 'Social Security Wages (Box 3)', social_security_tax_box4: 'SS Tax (Box 4)',
  medicare_wages_box5: 'Medicare Wages (Box 5)', medicare_tax_box6: 'Medicare Tax (Box 6)',
  state: 'State', state_wages: 'State Wages', state_tax: 'State Tax Withheld',
  payer_name: 'Payer Name', payer_tin: 'Payer TIN',
  recipient_name: 'Recipient Name', recipient_tin_last4: 'Recipient TIN (Last 4)',
  nonemployee_compensation_box1: 'Nonemployee Compensation (Box 1)',
  interest_income_box1: 'Interest Income (Box 1)',
  total_ordinary_dividends_box1a: 'Total Ordinary Dividends',
  qualified_dividends_box1b: 'Qualified Dividends',
  mortgage_interest_box1: 'Mortgage Interest (Box 1)',
  outstanding_principal_box2: 'Outstanding Principal (Box 2)',
  property_address: 'Property Address',
  early_withdrawal_penalty_box2: 'Early Withdrawal Penalty',
  federal_tax_withheld_box4: 'Federal Tax Withheld (Box 4)',
  year: 'Tax Year',
}

const MONEY_FIELDS = ['wages_box1','federal_tax_withheld_box2','social_security_wages_box3','social_security_tax_box4','medicare_wages_box5','medicare_tax_box6','state_wages','state_tax','nonemployee_compensation_box1','federal_tax_withheld_box4','interest_income_box1','total_ordinary_dividends_box1a','qualified_dividends_box1b','mortgage_interest_box1','outstanding_principal_box2','early_withdrawal_penalty_box2']

export default function DocumentAnalyzerPage() {
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [docType, setDocType] = useState('auto')
  const fileRef = useRef()

  function handleFile(file) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = e => {
      setImagePreview(e.target.result)
      const base64 = e.target.result.split(',')[1]
      setImage({ base64, mediaType: file.type, name: file.name })
    }
    reader.readAsDataURL(file)
    setResult(null)
    setError('')
  }

  function handleDrop(e) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  async function analyze() {
    if (!image) return
    setAnalyzing(true)
    setError('')
    setResult(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/ai-analyze-document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          imageBase64: image.base64,
          mediaType: image.mediaType,
          documentType: docType === 'auto' ? 'tax document' : docType
        })
      })
      const data = await res.json()
      if (data.error) { setError(data.error); setAnalyzing(false); return }
      setResult(data.result)
    } catch (err) {
      setError(err.message)
    }
    setAnalyzing(false)
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text)
  }

  const confidence = result?.confidence
  const confColor = confidence === 'high' ? '#10B981' : confidence === 'medium' ? '#F59E0B' : '#EF4444'

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 20, background: '#202326', border: 'none' }}>
        <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 4 }}>AI Document Analyzer</h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>Upload a W-2, 1099, 1098, or other tax document and AI will extract all the key values automatically.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: result ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Upload Panel */}
        <div>
          <div className="card" style={{ padding: 22, marginBottom: 16 }}>
            <label className="label" style={{ marginBottom: 8, display: 'block' }}>Document Type</label>
            <select className="input" value={docType} onChange={e => setDocType(e.target.value)} style={{ marginBottom: 16 }}>
              <option value="auto">Auto-Detect</option>
              <option value="W-2">W-2 (Wage and Tax Statement)</option>
              <option value="1099-NEC">1099-NEC (Nonemployee Compensation)</option>
              <option value="1099-INT">1099-INT (Interest Income)</option>
              <option value="1099-DIV">1099-DIV (Dividends)</option>
              <option value="1098">1098 (Mortgage Interest)</option>
            </select>

            {/* Drop zone */}
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileRef.current.click()}
              style={{
                border: '2px dashed #DED6CA', borderRadius: 12, padding: '32px 20px',
                textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
                background: imagePreview ? '#F7F3EB' : '#FAFAF8'
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#CBB17B'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#DED6CA'}
            >
              {imagePreview ? (
                <div>
                  <img src={imagePreview} alt="Document preview" style={{ maxHeight: 300, maxWidth: '100%', borderRadius: 8, objectFit: 'contain' }} />
                  <div style={{ fontSize: 12, color: '#62615D', marginTop: 10 }}>{image.name} · Click to change</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#202326', marginBottom: 6 }}>Drop your document here</div>
                  <div style={{ fontSize: 12, color: '#62615D' }}>or click to browse · JPG, PNG, PDF</div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />

            {error && (
              <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 12 }}>{error}</div>
            )}

            <button
              className="btn btn-primary"
              onClick={analyze}
              disabled={!image || analyzing}
              style={{ width: '100%', justifyContent: 'center', marginTop: 14, padding: 13, fontSize: 15 }}
            >
              {analyzing ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(201,168,76,0.3)', borderTopColor: '#CBB17B', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Analyzing document...
                </span>
              ) : '🔍 Analyze Document with AI'}
            </button>
          </div>

          <div className="card" style={{ padding: '14px 18px', background: '#F1E8D7', border: '1px solid #D8C18F' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#765B2C', marginBottom: 6 }}>📋 Supported Documents</div>
            <div style={{ fontSize: 12, color: '#765B2C', lineHeight: 1.8 }}>
              W-2 · 1099-NEC · 1099-INT · 1099-DIV · 1098 (Mortgage)<br />
              Best results with clear, well-lit photos or scans.<br />
              <strong>SSNs are partially masked for security.</strong>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        {result && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', background: '#202326', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#CBB17B', fontWeight: 700, fontSize: 15 }}>{result.document_type || 'Document'} Extracted</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>Tax Year: {result.year || 'Unknown'}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: confColor + '25', color: confColor, textTransform: 'capitalize' }}>
                {confidence} confidence
              </span>
            </div>

            {result.summary && (
              <div style={{ padding: '12px 20px', background: '#F7F3EB', borderBottom: '1px solid var(--border)', fontSize: 13, color: '#202326', lineHeight: 1.6 }}>
                💡 {result.summary}
              </div>
            )}

            <div style={{ padding: '8px 0', maxHeight: 480, overflow: 'auto' }}>
              {Object.entries(result)
                .filter(([k]) => !['confidence', 'document_type', 'summary', 'year'].includes(k) && result[k] !== null && result[k] !== undefined)
                .map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: '#62615D', flex: 1 }}>{FIELD_LABELS[key] || key}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#202326', textAlign: 'right' }}>
                        {MONEY_FIELDS.includes(key) && value ? `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : String(value)}
                      </div>
                      <button
                        onClick={() => copyToClipboard(String(value))}
                        title="Copy"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CBB17B', fontSize: 14, padding: '2px 4px' }}>
                        📋
                      </button>
                    </div>
                  </div>
                ))}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
              <button
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
                onClick={() => {
                  const text = Object.entries(result)
                    .filter(([k]) => !['confidence', 'document_type', 'summary'].includes(k) && result[k] !== null)
                    .map(([k, v]) => `${FIELD_LABELS[k] || k}: ${v}`)
                    .join('\n')
                  copyToClipboard(text)
                }}>
                📋 Copy All Values
              </button>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }} onClick={() => { setResult(null); setImage(null); setImagePreview(null) }}>
                Analyze Another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
